import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import FaqAccordion from '@/components/base/FaqAccordion';
import { useSEO } from '@/hooks/useSEO';
import { faqs } from '@/mocks/faqs';

const GROUP_ORDER = ['网站说明', '使用指南', '内容与更新'];

export default function FaqPage() {
  const groups = useMemo(() => {
    return GROUP_ORDER.map((name) => ({
      name,
      items: faqs.filter((faq) => faq.category === name),
    })).filter((group) => group.items.length > 0);
  }, []);

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页' },
          { '@type': 'ListItem', position: 2, name: '常见问题' },
        ],
      },
    ],
    [],
  );

  useSEO({
    title: '常见问题 — 时间线、反转与更新说明 | 西瓜黑料',
    description:
      '关于西瓜黑料的常见问题：时间线如何整理、后续瓜与反转有什么区别、结局会不会再更新、内容来源与订阅方式，一次讲清楚。',
    keywords: '西瓜黑料,西瓜黑料常见问题,西瓜黑料反转,西瓜黑料结局,吃瓜时间线说明',
    path: '/faq',
    jsonLd,
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 pb-10 pt-24 md:px-8 md:pb-14 md:pt-28">
        <div className="mx-auto max-w-6xl">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '常见问题' }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于时间线怎么整理、反转和后续瓜怎么区分、内容从哪里来、结局会不会继续更新，这里都有答案。还有疑问，欢迎写信给我们。
          </p>
        </div>
      </section>

      <section className="px-4 py-12 md:px-8 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8 space-y-10">
              {groups.map((group) => (
                <div key={group.name}>
                  <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                    {group.name}
                  </h2>
                  <div className="mt-5">
                    <FaqAccordion items={group.items} />
                  </div>
                </div>
              ))}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-5">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-900">
                    快速跳转
                  </h2>
                  <ul className="mt-4 space-y-3 text-sm">
                    {groups.map((group) => (
                      <li key={group.name}>
                        <span className="flex items-center gap-2 text-foreground-600">
                          <i className="ri-list-check-2 text-accent-600"></i>
                          {group.name}
                          <span className="text-xs text-foreground-400">
                            {group.items.length} 问
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg bg-primary-50 p-5">
                  <h2 className="font-heading text-base font-semibold text-primary-800">
                    还是没找到答案？
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    写下你的疑问，我们会尽量在一天内回复，并把高频问题补进这个页面。
                  </p>
                  <a
                    href="mailto:contact@xigua-heiliao.example.com"
                    rel="nofollow"
                    className="mt-4 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-mail-line"></i>
                    联系我们
                  </a>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-900">
                    继续追瓜
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    看懂了规则，就去瓜田里挑一出开始追吧。
                  </p>
                  <Link
                    to="/dramas"
                    className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    进入瓜田总览
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}